import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import AdminHeader from "../../header/admin_header";
import AdminSidebar from "../../sidebar/admin_sidebar";
import ReferenceLink from "../EmissionFactorBreakdown/ReferenceLink";
import { useGhgReferences } from "../EmissionFactorBreakdown/useGhgReferences";
import DatabaseForm from "./DatabaseForm";
import { useGhgDatabaseList } from "./useGhgDatabaseList";
import {
  EMPTY_FILTERS,
  FACTOR_BASES,
  LINK_FILTERS,
  SORTS,
  draftFromDatabase,
  emptyDraft,
  filterDatabases,
  summarizeDatabases,
} from "./helpers";
import "../KPIGrid/KPIGrid.css";
import "./GhgDatabases.css";

const BASIS_LABELS = Object.fromEntries(FACTOR_BASES.map(({ value, label }) => [value, label]));

/**
 * The GHG databases emission factors are quoted from: every database in
 * priority order with its basis, factor count and source link, filtered by
 * name, basis and link, with a form to add one or edit one (name, label,
 * priority, basis, price year and the source link from the reference
 * library, or a new one).
 */
export default function GhgDatabases() {
  const location = useLocation();
  const { databases, loading, error, reload } = useGhgDatabaseList();
  const { references, reload: reloadReferences } = useGhgReferences();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [formDraft, setFormDraft] = useState(null);
  const [notice, setNotice] = useState("");

  const shown = useMemo(() => filterDatabases(databases, filters), [databases, filters]);
  const stats = useMemo(() => summarizeDatabases(databases), [databases]);
  const filtered = filters.search || filters.basis || filters.link;

  const setFilter = (key) => (e) => {
    const { value } = e.target;
    setFilters((current) => ({ ...current, [key]: value }));
  };

  /** @param {import("./helpers").DatabaseDraft} draft */
  const openForm = (draft) => {
    setNotice("");
    setFormDraft(draft);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /** @param {import("./helpers").GhgDatabase} saved */
  const afterSave = (saved) => {
    setFormDraft(null);
    setNotice(`${saved.name} saved.`);
    reload();
  };

  return (
    <div>
      <AdminSidebar dataFromParent={location.pathname} />
      <AdminHeader />
      <div className="main_wrapper">
        <div className="inner_wraapper">
          <div className="kgb-root gdb-root">
            <div className="gdb-titlebar">
              <div>
                <h1 className="kgb-title">GHG Databases</h1>
                <p className="gdb-lead">The sources emission factors are quoted from, in the order companies are priced from them.</p>
              </div>
              {formDraft ? null : (
                <button type="button" className="kgb-btn kgb-btn--primary" onClick={() => openForm(emptyDraft())}>
                  + Add database
                </button>
              )}
            </div>

            {formDraft ? (
              <DatabaseForm
                key={formDraft.id ?? "new"}
                initial={formDraft}
                databases={databases}
                references={references}
                onReferencesChanged={reloadReferences}
                onSaved={afterSave}
                onCancel={() => setFormDraft(null)}
              />
            ) : null}

            {notice ? (
              <p className="gdb-notice" role="status">
                {notice}
              </p>
            ) : null}

            <div className="gdb-stats">
              <div className="gdb-stat">
                <span className="gdb-stat-value">{stats.total}</span>
                <span className="gdb-stat-label">Databases</span>
              </div>
              <div className="gdb-stat">
                <span className="gdb-stat-value">{stats.withFactors}</span>
                <span className="gdb-stat-label">Holding factors</span>
              </div>
              <div className="gdb-stat">
                <span className="gdb-stat-value">
                  {stats.withLink}
                  <small> / {stats.total}</small>
                </span>
                <span className="gdb-stat-label">With a source link</span>
              </div>
              <div className="gdb-stat">
                <span className="gdb-stat-value">{stats.spend}</span>
                <span className="gdb-stat-label">Spend based</span>
              </div>
            </div>

            <div className="kgb-card gdb-toolbar">
              <label className="gdb-field gdb-field--grow" htmlFor="gdb-search">
                <span>Search</span>
                <input id="gdb-search" type="search" value={filters.search} placeholder="Name or label" onChange={setFilter("search")} />
              </label>
              <label className="gdb-field" htmlFor="gdb-filter-basis">
                <span>Basis</span>
                <select id="gdb-filter-basis" value={filters.basis} onChange={setFilter("basis")}>
                  <option value="">Any basis</option>
                  {FACTOR_BASES.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="gdb-field" htmlFor="gdb-filter-link">
                <span>Source link</span>
                <select id="gdb-filter-link" value={filters.link} onChange={setFilter("link")}>
                  {LINK_FILTERS.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="gdb-field" htmlFor="gdb-sort">
                <span>Sort by</span>
                <select id="gdb-sort" value={filters.sort} onChange={setFilter("sort")}>
                  {SORTS.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              {filtered ? (
                <button type="button" className="kgb-btn kgb-btn--quiet" onClick={() => setFilters(EMPTY_FILTERS)}>
                  Clear filters
                </button>
              ) : null}
            </div>

            {error ? (
              <div className="gdb-message gdb-message--error">
                {error}{" "}
                <button type="button" className="kgb-btn kgb-btn--quiet" onClick={reload}>
                  Retry
                </button>
              </div>
            ) : null}
            {!error && loading ? <div className="gdb-message">Loading GHG databases…</div> : null}
            {!error && !loading && !shown.length ? (
              <div className="gdb-message">{filtered ? "No database matches these filters." : "No GHG databases yet."}</div>
            ) : null}

            {shown.length ? (
              <div className="kgb-card gdb-table-wrap">
                <table className="gdb-table">
                  <thead>
                    <tr>
                      <th scope="col" className="gdb-num">
                        Priority
                      </th>
                      <th scope="col">Database</th>
                      <th scope="col">Basis</th>
                      <th scope="col" className="gdb-num">
                        Factors
                      </th>
                      <th scope="col">Source link</th>
                      <th scope="col" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((database) => (
                      <tr key={database.id} className={formDraft?.id === database.id ? "gdb-row--editing" : undefined}>
                        <td className="gdb-num">
                          <span className="gdb-priority">{database.priority_order ?? "—"}</span>
                        </td>
                        <td>
                          <div className="gdb-name">{database.name}</div>
                          {database.source && database.source !== database.name ? (
                            <div className="gdb-muted">{database.source}</div>
                          ) : null}
                        </td>
                        <td>
                          <span className={`gdb-basis gdb-basis--${database.factor_basis.toLowerCase()}`}>
                            {BASIS_LABELS[database.factor_basis] || database.factor_basis}
                            {database.price_year ? ` · ${database.price_year}` : ""}
                          </span>
                        </td>
                        <td className="gdb-num">{database.factor_count.toLocaleString("en-IN")}</td>
                        <td>
                          {database.reference ? (
                            <ReferenceLink reference={database.reference} />
                          ) : (
                            <span className="gdb-missing">No link</span>
                          )}
                        </td>
                        <td className="gdb-actions">
                          <button
                            type="button"
                            className="kgb-btn"
                            aria-label={`Edit ${database.name}`}
                            onClick={() => openForm(draftFromDatabase(database))}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
